import axios from 'axios';
import { API_ENDPOINTS } from '../config/env';
import { getAccessToken } from '../utils/auth';

export interface SuperAgentFee {
  userId: number;
  percent: number;
  paymentFlow?: 'directCrypto' | 'fiatOnramp';
}

export interface AgentFee {
  userId: number;
  percent: number;
  paymentFlow?: 'directCrypto' | 'fiatOnramp';
}

export interface FeeSettings {
  superAgentFees: SuperAgentFee[];
  agentFees: AgentFee[];
}

export interface UpdateFeeRequest {
  type: 'Processor' | 'SuperAgent' | 'Agent';
  targetId: number;
  percent: number;
  paymentFlow?: 'directCrypto' | 'fiatOnramp';
}

export interface UpdateFeeResponse {
  updated: boolean;
}

export interface AddCustomFeeRequest {
  userId: number;
  feeType: 'SuperAgent' | 'Agent';
  percent: number;
  paymentFlow?: 'directCrypto' | 'fiatOnramp';
}

const normalizeFeeSettings = (raw: unknown): FeeSettings => {
  const o = ((raw as { data?: unknown })?.data ?? raw) as Record<string, unknown> | null;
  if (!o || typeof o !== 'object') return { superAgentFees: [], agentFees: [] };
  const fees = (keys: string[]) => {
    for (const k of keys) {
      if (Array.isArray(o[k])) return o[k];
    }
    return [];
  };
  return {
    superAgentFees: fees(['superAgentFees', 'SuperAgentFees']) as SuperAgentFee[],
    agentFees: fees(['agentFees', 'AgentFees']) as AgentFee[],
  };
};

class FeeSettingsService {
  private getAuthHeaders() {
    const token = getAccessToken();
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };
  }

  async getFeeSettings(): Promise<FeeSettings> {
    try {
      const response = await axios.get(API_ENDPOINTS.SETTINGS_FEES, {
        headers: this.getAuthHeaders(),
      });
      return normalizeFeeSettings(response.data);
    } catch (error) {
      console.error('Error fetching fee settings:', error);
      throw error;
    }
  }

  async updateFeeSettings(updateData: UpdateFeeRequest): Promise<UpdateFeeResponse> {
    try {
    
      const response = await axios.post(API_ENDPOINTS.SETTINGS_FEES, updateData, {
        headers: this.getAuthHeaders(),
      });
      
      
      return response.data;
    } catch (error) {
      console.error('Error updating fee settings:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  async addCustomFee(addData: AddCustomFeeRequest): Promise<UpdateFeeResponse> {
    try {
      
      const response = await axios.post(API_ENDPOINTS.SETTINGS_FEES, {
        type: addData.feeType,
        targetId: addData.userId,
        percent: addData.percent,
        paymentFlow: addData.paymentFlow,
      }, {
        headers: this.getAuthHeaders(),
      });
      
      return response.data;
    } catch (error) {

      throw error;
    }
  }
}

export const feeSettingsService = new FeeSettingsService();
